import { ApolloError } from '@apollo/client';
import { AssessmentAnswer } from '../../../generated/graphql';
import {
  isClientSignatureTypeAssessmentQuestion,
  isInitialsAssessmentAnswer,
  isSignatureAssessmentAnswer,
} from '../../lib/assessments';
import { HomecomingError, matchHomecomingError } from '../../../lib/errors';
import {
  PatientContactNewDraft,
  ProgramAssessmentQuestion,
} from '../../lib/program-assessment';

export const isIncompleteClientSignatureQuestion = (
  question: ProgramAssessmentQuestion,
  answer?: AssessmentAnswer,
): boolean => {
  const isNonCollaborativeSignatureQuestion =
    isClientSignatureTypeAssessmentQuestion(question);

  const isCompletedClientInitialsAnswer =
    answer && isInitialsAssessmentAnswer(answer) && answer.signatureName;

  const isCompletedClientSignatureAnswer =
    answer && isSignatureAssessmentAnswer(answer) && answer.signatureName;

  return (
    isNonCollaborativeSignatureQuestion &&
    !isCompletedClientInitialsAnswer &&
    !isCompletedClientSignatureAnswer
  );
};

export type CompleteProviderFormData = {
  // Support both single form (backward compatibility) and multiple forms
  providerSignatureName?: string;
  providerSignatureRole?: string;
  signatureTermsAgreed?: boolean;
  answers?: {
    value: string;
    values: string[];
    contactId?: string;
    newContactDraft?: PatientContactNewDraft;
  }[];
  // Multiple forms structure
  forms?: {
    [programAssessmentId: string]: {
      providerSignatureName: string;
      providerSignatureRole: string;
      signatureTermsAgreed: boolean;
      answers: {
        value: string;
        values: string[];
        contactId?: string;
        newContactDraft?: PatientContactNewDraft;
      }[];
    };
  };
};

/**
 * True when the provider-form query failed only because of a non-critical
 * `draftAnswers` field. The form can still be rendered in that case.
 */
export const isOnlyDraftAnswersError = (error?: ApolloError): boolean =>
  Boolean(
    error &&
      error.graphQLErrors?.every((err) =>
        err.path?.some(
          (path: string | number) =>
            path === 'draftAnswers' ||
            (typeof path === 'number' && err.path?.includes('draftAnswers')),
        ),
      ) &&
      !error.networkError,
  );

/**
 * True when the provider-form query failed for an *expected* authorization
 * reason — the signed-in user isn't allowed to view this form (stale link,
 * wrong account, post-logout, etc.). These are benign 403/404-style outcomes,
 * not code defects, so they must not be reported to Sentry as errors. They
 * follow the same `HomecomingError` pattern used across the app.
 */
export const isExpectedProviderFormError = (error?: ApolloError): boolean => {
  if (!error || error.networkError) return false;
  return Boolean(
    matchHomecomingError(error, HomecomingError.ResourceForbidden) ||
      matchHomecomingError(error, HomecomingError.ResourceNotFound),
  );
};
